import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  DialogContentText,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CommDataGrid, {
  titleDescriptionStyles,
  titleStyles,
} from "../components/CommDataGrid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { TextField } from "@mui/material";
import { useAlert } from "../context/AlertContext";
import { Menu, MenuItem } from "@mui/material";
import {
  createRank,
  getAllRanks,
  getUsersByRankId,
  removeRank,
  updateRank,
} from "../api/requests/ranks";
import CommonDialog from "../components/common/CommonDialog";
import { GridColDef } from "@mui/x-data-grid";
import { menuItemStyles, PaginationType } from "./Groups";
import UsersById, {
  dialogContextChildWrapperSx,
} from "../components/UsersByRankId";
import { createGroupByRank } from "../api/requests/groups";
import { useAuth } from "../context/AuthContext";
import { containedButtonWithIconSx } from "./Users";

//Images
import AddIcon from "../../src/assets/AddIcon.svg";
import NoRoleIcon from "../../src/assets/NoRoleBlue.svg";
import {
  datagridPropStyles,
  mainDivStyles,
  mainTitleStyles,
  noRoleSx,
  scrollContentStyles,
} from "./Roles";
import { subTitleStyles } from "../components/CreateRoleModal";
import { labelStyles } from "./createUser";
import CreateOrEditGroup from "../components/CreateOrEditGroup";

export interface Rank {
  id: number;
  rankName: string;
  count: number;
}

const Ranks = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState<boolean>(false);
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [rankName, setRankName] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState(""); // "add" or "edit"
  const [editRankId, setEditRankId] = useState<number | null>(null); // Track rank ID being edited
  const { showAlert } = useAlert();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [usersByRank, setUsersByRank] = useState([]);
  const openMenu = Boolean(anchorEl);
  const [deleteDialogId, setDeleteDialogId] = useState<number | null>(null);
  const [selectedRow, setSelectedRow] = useState<Rank | null>(null);
  const [ranksPaginationModel, setRanksPaginationModel] =
    useState<PaginationType>({
      page: 0,
      pageSize: 10,
    });
  const [ranksTotalRowsCount, setRanksTotalRowsCount] = useState<number>(0);
  const [ranksSearchText, setRanksSearchText] = useState<string>("");
  const [rankUsersTotalRowsCount, setRankUsersTotalRowsCount] =
    useState<number>(0);
  const [rankUsersPaginationModel, setRankUsersPaginationModel] =
    useState<PaginationType>({
      page: 0,
      pageSize: 10,
    });
  const [rankUsersSearchText, setRankUsersSearchText] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);

  const handleRankUserSearch = (value: string) => {
    setRankUsersSearchText(value.trim());
  };

  const closeDeleteDialog = () => {
    setRankName("");
    setSelectedRow(null);
    setDeleteDialogId(null);
  };

  const openDeleteDialog = (id: number) => {
    setDeleteDialogId(id);
    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Rank) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setRankName(row.rankName);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (selectedRow) {
      setOpen(true);
      setMode("edit");
      setEditRankId(selectedRow.id);
      setRankName(selectedRow.rankName || "");
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) {
      showAlert("No role selected", "error");
      return;
    }
    const roleId = selectedRow.id;
    try {
      const response = await removeRank(roleId);
      if (response && response.status === 200) {
        closeDeleteDialog();
        showAlert("Rank deleted successfully", "success");
        fetchAllRanks();
        setSelectedRow(null);
      }
    } catch (error: any) {
      console.error("Error deleting role:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete role";
      showAlert(errorMessage, "error");
      setSelectedRow(null);
    }
    handleMenuClose();
  };

  const handleCellClick = (params: any) => {
    if (params.field === "rankName" || params.field === "count") {
      setRankUsersPaginationModel({ page: 0, pageSize: 10 });
      setRankUsersSearchText("");
      setSelectedRow(params.row);
      setRankName(params.row.rankName);
    }
  };

  const handleCreateGroup = async (
    groupName: string,
    setGroupName: (text: string) => void
  ) => {
    if (!groupName.trim()) {
      showAlert("Group name cannot be empty.", "error");
      return;
    }
    if (rankUsersTotalRowsCount === 0) {
      showAlert("No users available to create a group.", "error");
      return;
    }
    try {
      const response =
        selectedRow &&
        (await createGroupByRank({
          rankId: selectedRow.id,
          groupName: groupName.trim(),
        }));
      if (response && response.status === 200) {
        showAlert("Group created successfully", "success");
        setGroupName("");
      }
    } catch (err) {
      console.error("Error creating group:", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "rankName",
      headerName: "Rank",
      flex: 1.5,
      headerClassName: "custom-header",
      width: 200,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ cursor: "pointer" }}>{params.value || "--"}</Box>
      ),
    },
    {
      field: "count",
      headerName: "Members",
      flex: 1,
      headerClassName: "custom-header",
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ display: "contents" }}>
            {params.value || 0} Members
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "custom-header",
      renderCell: (params) => (
        <>
          {user &&
            user.permissions.some(
              (p) =>
                p.module === "Users" &&
                (p.actions.includes("Edit") || p.actions.includes("Delete"))
            ) && (
              <IconButton
                onClick={(event) => {
                  handleMenuOpen(event, params.row);
                }}
              >
                <MoreVertIcon sx={{ color: "#64748B" }} />
              </IconButton>
            )}
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={menuItemStyles}
          >
            {user?.permissions && (
              <>
                {user.permissions.some(
                  (p) => p.module === "Users" && p.actions.includes("Edit")
                ) ? (
                  <MenuItem onClick={() => selectedRow && handleEdit()}>
                    Edit
                  </MenuItem>
                ) : null}
              </>
            )}{" "}
            {user?.permissions && (
              <>
                {user.permissions.some(
                  (p) => p.module === "Users" && p.actions.includes("Delete")
                ) ? (
                  <MenuItem
                    onClick={() =>
                      selectedRow && openDeleteDialog(params.row.id)
                    }
                  >
                    Delete
                  </MenuItem>
                ) : null}
              </>
            )}{" "}
          </Menu>
        </>
      ),
    },
  ];

  const handleOpen = () => {
    setMode("add");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setRankName("");
    setError("");
    setMode("");
    setEditRankId(null);
  };

  const handleSave = async () => {
    if (!rankName.trim()) {
      setError("Rank name is required.");
      return;
    }
    try {
      if (mode === "edit" && editRankId) {
        // Update existing rank
        const response = await updateRank(editRankId, rankName.trim());
        if (response.status === 200) {
          handleClose();
          await fetchAllRanks();
          showAlert("Rank updated successfully!", "success");
        } else {
          showAlert("Failed to update rank.", "error");
        }
      } else {
        // Create new rank
        const response = await createRank(rankName.trim());
        if (response.status === 200) {
          showAlert("Rank added successfully!", "success");
          setRanksPaginationModel((prev) => ({ ...prev, page: 0 }));
          handleClose();
          await fetchAllRanks();
        } else {
          showAlert("Failed to add rank.", "error");
        }
      }
    } catch {
      showAlert(
        `An error occurred while ${
          mode === "edit" ? "updating" : "adding"
        } the rank.`,
        "error"
      );
    }
  };

  const fetchAllRanks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllRanks({
        page: ranksPaginationModel.page,
        size: ranksPaginationModel.pageSize,
        q: ranksSearchText.trim(),
      });
      if (response && response.data && response.data.ranks) {
        const updatedRanks = response.data.ranks.map((rank: any) => ({
          id: rank.rankId,
          rankName: rank.rankName,
          count: rank.usersCount,
        }));
        const ids = updatedRanks.map((g: any) => g.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.error("Duplicate  IDs detected:", ids);
        }
        setRanks(updatedRanks);
        setRanksTotalRowsCount(response.data.totalCount);
      } else {
        showAlert("Failed to fetch ranks.", "error");
      }
    } catch {
      showAlert("An error occurred while fetching ranks.", "error");
    } finally {
      setLoading(false);
    }
  }, [ranksPaginationModel, ranksSearchText]);

  const handleSearch = useCallback((value: string) => {
    setRanksSearchText(value.trim());
    setRanksPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const fetchUsersByRank = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response =
        selectedRow &&
        (await getUsersByRankId({
          rankId: selectedRow.id,
          page: rankUsersPaginationModel.page,
          size: rankUsersPaginationModel.pageSize,
          q: rankUsersSearchText.trim(),
        }));
      if (response && response.data && response.data.users) {
        setUsersByRank(response.data.users);
        setRankUsersTotalRowsCount(response.data.totalCount);
      }
    } catch {
      console.error("Error fetching users by rank");
    } finally {
      setUsersLoading(false);
    }
  }, [rankUsersPaginationModel, rankUsersSearchText, selectedRow]);

  // Memoize rows to stabilize prop
  const memoizedRows = useMemo(() => ranks, [ranks]);
  const memoizedUserRows = useMemo(() => usersByRank, [usersByRank]);

  useEffect(() => {
    fetchAllRanks();
  }, [fetchAllRanks]);

  useEffect(() => {
    if (selectedRow) {
      fetchUsersByRank();
    }
  }, [fetchUsersByRank, selectedRow]);

  const buttonsComponent = useCallback(
    () => (
      <>
        {" "}
        {user?.permissions && (
          <>
            {user.permissions.some(
              (p) => p.module === "Users" && p.actions.includes("Create")
            ) ? (
              <Button
                variant="contained"
                onClick={handleOpen}
                sx={containedButtonWithIconSx}
              >
                <img src={AddIcon} /> Add Rank
              </Button>
            ) : null}
          </>
        )}{" "}
      </>
    ),
    [user]
  );

  return (
    <Box sx={mainDivStyles}>
      <Box sx={mainTitleStyles}>
        <Typography sx={titleStyles}>Rank Management</Typography>
        <Typography sx={titleDescriptionStyles}>
          View and manage all ranks in the system
        </Typography>
      </Box>
      {/* Scrollable Content */}
      <Box sx={scrollContentStyles}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5.8} sx={{ pt: 0 }}>
            <CommDataGrid
              sx={datagridPropStyles}
              onRowClick={undefined}
              onCellClick={handleCellClick}
              pageSize={10}
              columns={columns}
              rows={memoizedRows}
              title=""
              description="Select a rank to view its users"
              section="Ranks"
              renderButtons={buttonsComponent}
              setPaginationModel={setRanksPaginationModel}
              paginationModel={ranksPaginationModel}
              totalRowsCount={ranksTotalRowsCount}
              searchText={ranksSearchText}
              handleSearch={handleSearch}
              loading={loading}
            />
          </Grid>
          <Grid item xs="auto">
            <Divider orientation="vertical" sx={{ height: "96%" }} />
          </Grid>
          <Grid item xs={12} lg={5.8}>
            {selectedRow ? (
              <UsersById
                section="Ranks"
                users={memoizedUserRows}
                rankName={rankName}
                fetchUsersByRank={fetchUsersByRank}
                onUserDelete={fetchAllRanks}
                setPaginationModel={setRankUsersPaginationModel}
                paginationModel={rankUsersPaginationModel}
                totalRowsCount={rankUsersTotalRowsCount}
                searchText={rankUsersSearchText}
                handleSearch={handleRankUserSearch}
                selectedRow={selectedRow}
                loading={usersLoading}
              />
            ) : (
              <Box sx={noRoleSx}>
                <img
                  src={NoRoleIcon}
                  alt="No Role"
                  style={{ height: "120px", width: "120px" }}
                />
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 400, color: "#000000" }}
                >
                  Select a rank to view the users assigned to it.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
      {open && (
        <CommonDialog
          title="Add Rank"
          cancelButton={handleClose}
          actionButton={handleSave}
          actionButtonTitle={mode === "edit" ? "Update" : "Save"}
          cancelButtonTitle="Cancel"
        >
          <DialogContentText>
            <Box sx={dialogContextChildWrapperSx}>
              <Typography sx={subTitleStyles}>
                {mode === "edit" ? "Edit Rank" : "Add Rank"}{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter rank name"
                value={rankName}
                onChange={(e) => {
                  setRankName(e.target.value);
                  setError("");
                }}
                error={!!error}
                helperText={error}
                slotProps={{
                  input: {
                    sx: labelStyles,
                  },
                }}
              />
            </Box>
          </DialogContentText>
        </CommonDialog>
      )}
      {!!deleteDialogId && (
        <CommonDialog
          title="Delete Rank"
          cancelButton={closeDeleteDialog}
          actionButton={handleDelete}
          actionButtonTitle="Yes"
          cancelButtonTitle="cancel"
        >
          <DialogContentText>
            <Typography
              fontSize="13px"
              sx={{ color: "#000000", paddingBottom: 1 }}
            >
              Are you sure you want to delete the rank?
            </Typography>
            <Typography fontSize="12px">
              {`The rank will be deleted if you click on 'Yes'.`}
            </Typography>
          </DialogContentText>
        </CommonDialog>
      )}
    </Box>
  );
};

export default Ranks;
